/**
 * OnboardingSettingsDrawer — project-wide funnel settings next to the Onboardings list.
 *
 * Right drawer "Onboarding settings" (FP-3 page + drawer) opened from the gear in the Onboardings
 * header. Hosts Font Mappings and Locale Configuration — the same panels and the same accordion test
 * ids they had on Integrations (sidebar plan 2026-10-07, C-12 / C-14 / C-16, D-28). The content is
 * unmounted while the drawer is closed.
 */

import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Language as LanguageIcon,
  TextFields as FontIcon,
} from '@mui/icons-material';
import { FontConfigPanel } from '@modules/font-config';
import { LocaleConfigPanel } from '@modules/locale-config';
import { ONBOARDING_SETTINGS_TEST_IDS } from '../testIds';
import {
  ONBOARDING_SETTINGS_SECTIONS,
  type OnboardingSettingsSection,
} from '../hooks/useOnboardingSettingsParam';

export const ONBOARDING_SETTINGS_DRAWER_WIDTH = 640;
/**
 * Width while Font Mappings is open: its slot × platform table needs ~1,220 px (7 columns), so the
 * drawer widens instead of hiding half the table behind a sideways scroll (REVIEW 2026-10-08, P1).
 */
export const ONBOARDING_SETTINGS_DRAWER_WIDE = 'min(1240px, calc(100vw - 48px))';

const TITLE_ID = 'onb-settings-drawer-title';

const SECTION_META: Record<OnboardingSettingsSection, {
  title: string;
  description: string;
  icon: typeof FontIcon;
  sectionTestId: string;
  contentTestId: string;
}> = {
  fonts: {
    title: 'Font Mappings',
    description: 'Configure per-platform font mappings for web funnels',
    icon: FontIcon,
    sectionTestId: ONBOARDING_SETTINGS_TEST_IDS.sectionFonts,
    contentTestId: ONBOARDING_SETTINGS_TEST_IDS.contentFonts,
  },
  locales: {
    title: 'Locale Configuration',
    description: 'Manage shared project languages for funnels, onboarding flows, and creatives',
    icon: LanguageIcon,
    sectionTestId: ONBOARDING_SETTINGS_TEST_IDS.sectionLocales,
    contentTestId: ONBOARDING_SETTINGS_TEST_IDS.contentLocales,
  },
};

export interface OnboardingSettingsDrawerProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  /** Section to expand when the drawer opens (deep link `?settings=<section>`). */
  initialSection?: OnboardingSettingsSection | null;
  /** Sections the viewer may see; defaults to all. An empty list shows the empty state. */
  sections?: readonly OnboardingSettingsSection[];
}

export function OnboardingSettingsDrawer({
  projectId,
  open,
  onClose,
  initialSection = null,
  sections = ONBOARDING_SETTINGS_SECTIONS,
}: OnboardingSettingsDrawerProps) {
  const [expanded, setExpanded] = useState<OnboardingSettingsSection[]>(initialSection ? [initialSection] : []);

  // Each opening starts collapsed except the deep-linked section.
  useEffect(() => {
    if (open) setExpanded(initialSection ? [initialSection] : []);
  }, [open, initialSection]);

  const toggle = (section: OnboardingSettingsSection) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded((prev) => (isExpanded ? [...prev, section] : prev.filter((key) => key !== section)));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': TITLE_ID,
          // Width switches instantly (no width animation: layout-property transitions thrash layout).
          sx: {
            width: expanded.includes('fonts') ? ONBOARDING_SETTINGS_DRAWER_WIDE : ONBOARDING_SETTINGS_DRAWER_WIDTH,
            maxWidth: '100%',
          },
        },
      }}
    >
      <Box
        data-testid={ONBOARDING_SETTINGS_TEST_IDS.drawer}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Close"
            data-testid={ONBOARDING_SETTINGS_TEST_IDS.close}
          >
            <CloseIcon />
          </IconButton>
          <Typography id={TITLE_ID} variant="h6" component="h2">
            Onboarding settings
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Settings shared by every onboarding in this project.
          </Typography>
          {sections.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid={ONBOARDING_SETTINGS_TEST_IDS.empty}
              sx={{ py: 4, textAlign: 'center' }}
            >
              No onboarding settings are available on this plan.
            </Typography>
          ) : (
            <Box>
              {sections.map((section) => {
                const meta = SECTION_META[section];
                const Icon = meta.icon;
                return (
                  <Accordion
                    key={section}
                    expanded={expanded.includes(section)}
                    onChange={toggle(section)}
                    data-testid={meta.sectionTestId}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Icon color="action" />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {meta.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {meta.description}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails data-testid={meta.contentTestId}>
                      {section === 'fonts'
                        ? <FontConfigPanel projectId={projectId} />
                        : <LocaleConfigPanel projectId={projectId} />}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default OnboardingSettingsDrawer;
